import type { MouseEvent } from 'react'
import { Lock } from 'lucide-react'
import type { WireArticle } from '../../../api/types'
import { useWireAuth } from '../../../auth/useWireAuth'
import { isArticleLocked } from '../../../lib/articleAccess'

export type RankedListSectionHeaderStyle = 'rule-left' | 'all-caps' | 'plain'
export type RankedListNumberingStyle = 'numeric' | 'pill' | 'minimal'

export interface RankedListConfig {
  sectionHeaderEnabled: boolean
  sectionHeaderTitle: string | null
  sectionHeaderStyle: RankedListSectionHeaderStyle
  numberingStyle: RankedListNumberingStyle
  showSummary: boolean
  showFooterAction: boolean
  footerActionLabel: string
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

export interface RankedListProps {
  articles: WireArticle[]
  config?: Partial<RankedListConfig>
  className?: string
  onArticleClick?: (article: WireArticle, index: number, event: MouseEvent<HTMLElement>) => void
  onFooterActionClick?: (event: MouseEvent<HTMLButtonElement>) => void
  activeArticleId?: string | null
}

const DEFAULT_CONFIG: RankedListConfig = {
  sectionHeaderEnabled: false,
  sectionHeaderTitle: null,
  sectionHeaderStyle: 'rule-left',
  numberingStyle: 'numeric',
  showSummary: false,
  showFooterAction: false,
  footerActionLabel: 'Explore More',
}

function renderSectionHeader(title: string, style: RankedListSectionHeaderStyle) {
  if (style === 'all-caps') {
    return (
      <header className="mb-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-fg-secondary">{title}</h3>
      </header>
    )
  }
  if (style === 'plain') {
    return (
      <header className="mb-3">
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
      </header>
    )
  }
  // rule-left (default)
  return (
    <header className="mb-3 border-b border-line pb-1">
      <h3 className="text-xs font-bold uppercase tracking-wider text-fg-secondary">{title}</h3>
    </header>
  )
}

function NumericNumber({ n }: { n: number }) {
  return (
    <span className="w-8 shrink-0 text-right text-2xl font-black leading-none tabular-nums text-fg-secondary select-none">
      {n}
    </span>
  )
}

function PillNumber({ n }: { n: number }) {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-fg text-[10px] font-black tabular-nums text-canvas select-none">
      {n}
    </span>
  )
}

function MinimalNumber({ n }: { n: number }) {
  return (
    <span className="w-4 shrink-0 text-right text-xs font-black tabular-nums text-fg-secondary select-none">
      {n}
    </span>
  )
}

function RankedItem({
  article,
  index,
  locked,
  numberingStyle,
  showSummary,
  onArticleClick,
  activeArticleId,
}: {
  article: WireArticle
  index: number
  locked?: boolean
  numberingStyle: RankedListNumberingStyle
  showSummary: boolean
  onArticleClick?: (article: WireArticle, index: number, event: MouseEvent<HTMLElement>) => void
  activeArticleId?: string | null
}) {
  const n = index + 1

  const number =
    numberingStyle === 'pill' ? (
      <PillNumber n={n} />
    ) : numberingStyle === 'minimal' ? (
      <MinimalNumber n={n} />
    ) : (
      <NumericNumber n={n} />
    )

  const isNumeric = numberingStyle === 'numeric'
  const isMinimal = numberingStyle === 'minimal'

  return (
    <article
      className={`flex cursor-pointer gap-3 border-b border-line transition-all duration-200 last:border-0 hover:bg-fill-hover/40 ${
        isNumeric ? 'items-start py-3 px-2' : isMinimal ? 'items-start py-2 px-2' : 'items-center py-2.5 px-2'
      } ${activeArticleId === article.id ? 'bg-accent-primary-fill/45 ring-2 ring-inset ring-accent-primary-solid shadow-sm' : ''} ${activeArticleId && activeArticleId !== article.id ? 'opacity-35 saturate-50' : ''}`}
      onClick={(event) => onArticleClick?.(article, index, event)}
    >
      {number}
      <div className="min-w-0 flex-1 border-l border-line pl-3">
        <h4
          className={`font-zodiak font-bold leading-snug text-fg ${
            isNumeric ? 'text-sm' : 'text-xs'
          }`}
        >
          {locked && <Lock size={11} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}
          ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.workflow?.stage === 'recalled' ? 'This Article Has Been Recalled' : article.title}
        </h4>
        {article.sensitive ? (
          <p className="mt-0.5 inline-flex items-center rounded bg-red-9 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white">
            Sensitive
          </p>
        ) : null}
        {showSummary && article.description ? (
          <p className="mt-1 text-xs leading-relaxed text-fg-secondary">{article.description}</p>
        ) : null}
        <p className="mt-1 text-[9px] uppercase tracking-wide text-fg-secondary">
          {article.regions?.[0] && !isMinimal ? <>{article.regions[0]} · </> : null}{formatDate(article.publishedAt ?? article.updatedAt)} · 3 MIN
        </p>
      </div>
    </article>
  )
}

export function RankedList({ articles, config, className, onArticleClick, onFooterActionClick, activeArticleId }: RankedListProps) {
  const { user } = useWireAuth()
  const merged = { ...DEFAULT_CONFIG, ...config }

  return (
    <section className={className}>
      {merged.sectionHeaderEnabled && merged.sectionHeaderTitle
        ? renderSectionHeader(merged.sectionHeaderTitle, merged.sectionHeaderStyle)
        : null}

      <div>
        {articles.map((article, index) => (
          <RankedItem
            key={article.id}
            article={article}
            index={index}
            locked={Boolean(user && isArticleLocked(article, user))}
            numberingStyle={merged.numberingStyle}
            showSummary={merged.showSummary}
            onArticleClick={onArticleClick}
            activeArticleId={activeArticleId}
          />
        ))}
      </div>

      {merged.showFooterAction ? (
        <div className="mt-3 border-t border-line pt-3">
          <button
            type="button"
            onClick={onFooterActionClick}
            className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary transition-colors hover:text-fg"
          >
            {merged.footerActionLabel} ↗
          </button>
        </div>
      ) : null}
    </section>
  )
}
